import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router";
import { Eye, EyeOff, CheckCircle } from "lucide-react";
import api from "../../services/api";

export default function ConfirmInvite() {
    const [searchParams] = useSearchParams();
    const { token: routeToken } = useParams<{ token?: string }>();
    const navigate = useNavigate();
    const queryToken = searchParams.get("token");
    const token = routeToken || queryToken || null;

    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [isValid, setIsValid] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        password: "",
        confirmPassword: ""
    });

    const [errors, setErrors] = useState({
        name: "",
        password: "",
        confirmPassword: ""
    });

    // Verificar validade do token ao carregar
    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setVerifying(false);
                setIsValid(false);
                return;
            }

            try {
                // Tentar buscar informações do convite (você pode criar um endpoint específico para isso)
                // Por enquanto, vamos apenas validar que existe
                setIsValid(true);
                // Aqui você poderia fazer uma chamada para pegar os dados do convite
                // const response = await api.get(`/api/collaborators/invite/${token}`);
                // setInviteData(response.data.data);
            } catch (error) {
                console.error("Token inválido:", error);
                setIsValid(false);
            } finally {
                setVerifying(false);
            }
        };

        verifyToken();
    }, [token]);

    const validateForm = () => {
        const newErrors = {
            name: "",
            password: "",
            confirmPassword: ""
        };

        if (!formData.name.trim()) {
            newErrors.name = "Nome é obrigatório";
        }

        if (!formData.password) {
            newErrors.password = "Senha é obrigatória";
        } else if (formData.password.length < 6) {
            newErrors.password = "A senha deve ter no mínimo 6 caracteres";
        }

        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = "As senhas não coincidem";
        }

        setErrors(newErrors);
        return !newErrors.name && !newErrors.password && !newErrors.confirmPassword;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        try {
            await api.post('/api/collaborators/confirm', {
                token,
                password: formData.password,
                name: formData.name
            });

            setSuccess(true);

            // Redirecionar para login após 3 segundos
            setTimeout(() => {
                navigate('/signin');
            }, 3000);
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || 'Erro ao confirmar convite';
            alert(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    if (verifying) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-100 dark:bg-gray-900">
                <div className="text-center">
                    <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Verificando convite...</p>
                </div>
            </div>
        );
    }

    if (!isValid || !token) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-100 dark:bg-gray-900 px-4">
                <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl dark:bg-gray-800">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                        <svg className="h-8 w-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                    <h2 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
                        Convite Inválido
                    </h2>
                    <p className="mb-6 text-gray-600 dark:text-gray-400">
                        Este convite é inválido ou já foi utilizado. Entre em contato com quem te convidou para solicitar um novo convite.
                    </p>
                    <button
                        onClick={() => navigate('/signin')}
                        className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    >
                        Ir para Login
                    </button>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-100 dark:bg-gray-900 px-4">
                <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl dark:bg-gray-800">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                        <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
                    </div>
                    <h2 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
                        Convite Confirmado!
                    </h2>
                    <p className="mb-6 text-gray-600 dark:text-gray-400">
                        Sua conta foi criada com sucesso. Você será redirecionado para fazer login...
                    </p>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                        <div className="h-full w-full animate-pulse bg-blue-600"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-100 dark:bg-gray-900 px-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="mb-8 text-center">
                    <img
                        src="/images/logo/logo.svg"
                        alt="Sellyo"
                        className="mx-auto h-10 dark:hidden"
                    />
                    <img
                        src="/images/logo/logo-dark.svg"
                        alt="Sellyo"
                        className="mx-auto hidden h-10 dark:block"
                    />
                </div>

                {/* Card */}
                <div className="rounded-2xl bg-white p-8 shadow-xl dark:bg-gray-800">
                    <div className="mb-6 text-center">
                        <h2 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
                            Confirmar Convite
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Complete o cadastro para aceitar o convite
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Nome */}
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Nome completo
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Seu nome completo"
                                className={`w-full rounded-lg border ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                                    } bg-white px-4 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400`}
                            />
                            {errors.name && (
                                <p className="mt-1 text-xs text-red-500">{errors.name}</p>
                            )}
                        </div>

                        {/* Senha */}
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Senha
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    placeholder="Mínimo 6 caracteres"
                                    className={`w-full rounded-lg border ${errors.password ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                                        } bg-white px-4 py-2.5 pr-10 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                            {errors.password && (
                                <p className="mt-1 text-xs text-red-500">{errors.password}</p>
                            )}
                        </div>

                        {/* Confirmar Senha */}
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Confirmar senha
                            </label>
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={formData.confirmPassword}
                                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                    placeholder="Digite a senha novamente"
                                    className={`w-full rounded-lg border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                                        } bg-white px-4 py-2.5 pr-10 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                            {errors.confirmPassword && (
                                <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>
                            )}
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 disabled:opacity-50 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                        >
                            {loading ? (
                                <span className="flex items-center justify-center">
                                    <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Confirmando...
                                </span>
                            ) : (
                                'Confirmar e criar conta'
                            )}
                        </button>

                        <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                            Ao confirmar, você concorda com os{' '}
                            <a href="#" className="text-blue-600 hover:underline dark:text-blue-400">
                                Termos de Uso
                            </a>
                        </p>
                    </form>
                </div>

                {/* Link para login */}
                <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
                    Já tem uma conta?{' '}
                    <button
                        onClick={() => navigate('/signin')}
                        className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                    >
                        Fazer login
                    </button>
                </p>
            </div>
        </div>
    );
}
